"use client";

import type { Transition } from "motion/react";
import React from "react";
import MotionHighlight from "@/components/effects/MotionHighlight";
import Flex from "@/components/layout/Flex";
import Typography from "@/components/shared/Typography";
import Button, { buttonVariants } from "@/components/ui/Button";
import NavigationMenu from "@/components/ui/NavigationMenu";
import {
  getIndustries,
  getServiceCategories,
  getServiceCategory,
} from "@/content/helpers";
import type { ServiceCategoryKey } from "@/content/types";
import { URLS } from "@/lib/consts";
import { breakpointClasses, cn, splitBreakpointProps } from "@/lib/utils";
import type { DesktopNavProps } from "../types";
import { ListItem } from ".";

const INDUSTRIES = getIndustries();
const SERVICE_CATEGORIES = getServiceCategories();
const DEFAULT_TRANSITION: Transition = { duration: 0.2 };

function DesktopNav({
  transition = DEFAULT_TRANSITION,
  animateOnHover = true,
  className,
  ...props
}: DesktopNavProps) {
  const [selectedCategory, setSelectedCategory] =
    React.useState<ServiceCategoryKey>("accounting");
  const { bp, native } = splitBreakpointProps(props);
  const classes = breakpointClasses(bp);

  const handleOnMouseEnter = React.useCallback((title: string) => {
    setSelectedCategory(title as ServiceCategoryKey);
  }, []);

  return (
    <NavigationMenu
      className={cn("hidden md:flex", className, classes)}
      viewport={false}
      {...native}
    >
      <MotionHighlight
        hover
        controlledItems
        className="rounded-sm bg-accent"
        transition={transition}
        enabled={animateOnHover}
      >
        <NavigationMenu.List>
          <NavigationMenu.Item>
            <MotionHighlight.Item>
              <NavigationMenu.Trigger
                className={buttonVariants({ variant: "link" })}
              >
                Services
              </NavigationMenu.Trigger>
            </MotionHighlight.Item>

            <NavigationMenu.Content>
              <Flex.Row
                gap="gap-0"
                className="w-[400px] md:w-[600px] lg:w-[800px] max-h-[75vh]"
              >
                <MotionHighlight
                  hover
                  controlledItems
                  className="rounded-sm bg-accent"
                  transition={transition}
                  enabled={animateOnHover}
                >
                  <ul className="flex flex-col w-full h-full">
                    {SERVICE_CATEGORIES.map(
                      ({ key, imageUrl, imageAlt, ...item }) => (
                        <MotionHighlight.Item key={key}>
                          <ListItem
                            value={key}
                            selectedCategory={selectedCategory}
                            onMouseEnter={handleOnMouseEnter}
                            {...item}
                          />
                        </MotionHighlight.Item>
                      ),
                    )}
                  </ul>
                </MotionHighlight>

                <ul className="flex flex-col w-full h-auto self-stretch bg-accent rounded-r-sm border-ring border-l overflow-y-auto">
                  {getServiceCategory(selectedCategory).items.map(item => (
                    <li key={item.key}>
                      <NavigationMenu.Link asChild>
                        <Button.Link
                          className="h-auto w-auto"
                          variant="link"
                          href={item.href}
                          aria-label={`Goto ${item.label} Page`}
                        >
                          <Typography.Small className="text-left text-sm leading-none font-medium w-full">
                            {item.label}
                          </Typography.Small>

                          <Typography.Small className="text-left text-muted-foreground line-clamp-2 text-sm leading-snug w-full">
                            {item.description}
                          </Typography.Small>
                        </Button.Link>
                      </NavigationMenu.Link>
                    </li>
                  ))}
                </ul>
              </Flex.Row>
            </NavigationMenu.Content>
          </NavigationMenu.Item>

          <NavigationMenu.Item>
            <MotionHighlight.Item>
              <NavigationMenu.Trigger
                className={buttonVariants({ variant: "link" })}
              >
                Industries
              </NavigationMenu.Trigger>
            </MotionHighlight.Item>

            <NavigationMenu.Content>
              <MotionHighlight
                hover
                controlledItems
                className="rounded-sm bg-accent"
                transition={transition}
                enabled={animateOnHover}
              >
                <ul className="grid w-[300px] gap-2 md:grid-cols-2 md:w-[400px] lg:w-[500px] max-h-[75vh] overflow-y-auto">
                  {INDUSTRIES.map(
                    ({ key, imageUrl, imageAlt, relatedKeys, ...item }) => (
                      <MotionHighlight.Item key={key}>
                        <ListItem
                          value={key}
                          selectedCategory={selectedCategory}
                          {...item}
                        />
                      </MotionHighlight.Item>
                    ),
                  )}
                </ul>
              </MotionHighlight>
            </NavigationMenu.Content>
          </NavigationMenu.Item>

          <NavigationMenu.Item>
            <MotionHighlight.Item>
              <NavigationMenu.Link asChild>
                <Button.Link
                  variant="link"
                  href={URLS.ABOUT}
                  className="w-max"
                  aria-label="Goto About Page"
                >
                  About
                </Button.Link>
              </NavigationMenu.Link>
            </MotionHighlight.Item>
          </NavigationMenu.Item>

          <NavigationMenu.Item>
            <MotionHighlight.Item>
              <NavigationMenu.Link asChild>
                <Button.Link
                  variant="link"
                  href={URLS.CONTACT_US}
                  className="w-max"
                  aria-label="Goto Contact Us Page"
                >
                  Contact Us
                </Button.Link>
              </NavigationMenu.Link>
            </MotionHighlight.Item>
          </NavigationMenu.Item>

          <NavigationMenu.Item>
            <NavigationMenu.Link asChild>
              <Button.Link
                variant="secondary"
                href={"https://orsakcpas.clientportal.com/#/login"}
                className="w-max"
                aria-label="Goto Client Portal"
              >
                Client Portal
              </Button.Link>
            </NavigationMenu.Link>
          </NavigationMenu.Item>
        </NavigationMenu.List>
      </MotionHighlight>
    </NavigationMenu>
  );
}

export default DesktopNav;
